import { z } from 'zod';
import type { WorkflowTriggerOption } from '../../trigger/hooks';

const uuidSchema = z.string().uuid();

/**
 * URL query keys that carry an existing trigger into the "Create new workflow"
 * seed, so the new workflow opens pre-bound to that trigger instead of blank.
 * Stripped on persist (see the workflow strategy's `extraStripQueryKeys`).
 */
export const WORKFLOW_SEED_TRIGGER_QUERY_KEYS = [
  'triggerId',
  'triggerKind',
  'triggerName',
  'triggerSchedule',
  'triggerEventName',
] as const;

/** Minimal trigger identity needed to pre-bind an existing trigger to a draft. */
export type SeedTrigger = Pick<
  WorkflowTriggerOption,
  'id' | 'kind' | 'name' | 'schedule' | 'eventName'
>;

/** Encodes an existing trigger into the seed URL params. */
export function encodeSeedTriggerParams(
  params: URLSearchParams,
  trigger: SeedTrigger
): void {
  params.set('triggerId', trigger.id);
  params.set('triggerKind', trigger.kind);
  if (trigger.name !== '') {
    params.set('triggerName', trigger.name);
  }
  if (trigger.schedule !== null && trigger.schedule !== '') {
    params.set('triggerSchedule', trigger.schedule);
  }
  if (
    trigger.eventName !== undefined &&
    trigger.eventName !== null &&
    trigger.eventName !== ''
  ) {
    params.set('triggerEventName', trigger.eventName);
  }
}

/**
 * Reads a seed trigger from the chat-seed URL, or `undefined` when absent or
 * malformed. Only `cron`/`webhook`/`event` kinds are accepted since those are
 * the kinds the draft graph can pre-bind.
 */
export function parseSeedTrigger(
  searchParams: URLSearchParams
): WorkflowTriggerOption | undefined {
  const id = searchParams.get('triggerId');
  if (id === null || !uuidSchema.safeParse(id).success) {
    return undefined;
  }
  const kind = searchParams.get('triggerKind');
  if (kind !== 'cron' && kind !== 'webhook' && kind !== 'event') {
    return undefined;
  }
  const schedule = searchParams.get('triggerSchedule');
  const eventName = searchParams.get('triggerEventName');
  return {
    id,
    kind,
    name: searchParams.get('triggerName') ?? '',
    schedule: schedule === null || schedule === '' ? null : schedule,
    eventName: eventName === null || eventName === '' ? null : eventName,
  };
}
